import React from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '../../../shared/components';
import { listCommentAutomationContentOptions } from '../services/commentAutomationService';
import type {
  CommentAutomationChannel,
  CommentAutomationContentOption,
} from '../types/CommentAutomation';
import styles from './CommentAutomationPostPicker.module.css';

type CommentAutomationPostPickerProps = {
  channel: CommentAutomationChannel;
  channelAccountId?: string | null;
  value: string;
  onChange: (externalContentId: string, option: CommentAutomationContentOption | null) => void;
  disabled?: boolean;
};

const summarizeCaption = (caption?: string | null): string => {
  const trimmed = caption?.trim();
  if (!trimmed) {
    return '';
  }
  return trimmed.length > 90 ? `${trimmed.slice(0, 87)}…` : trimmed;
};

const contentTypeLabelKey = (option: CommentAutomationContentOption): string => {
  const product = option.mediaProductType?.trim().toUpperCase();
  if (product === 'REELS' || product === 'REEL') {
    return 'postTypeReel';
  }
  if (product === 'STORY') {
    return 'postTypeStory';
  }
  const type = option.contentType?.trim().toUpperCase();
  if (type === 'VIDEO') {
    return 'postTypeVideo';
  }
  if (type === 'CAROUSEL_ALBUM' || type === 'CAROUSEL') {
    return 'postTypeCarousel';
  }
  return 'postTypePost';
};

export const CommentAutomationPostPicker: React.FC<CommentAutomationPostPickerProps> = ({
  channel,
  channelAccountId,
  value,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation('commentAutomations');
  const [isOpen, setIsOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<CommentAutomationContentOption[]>([]);
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [selectedPreview, setSelectedPreview] = React.useState<CommentAutomationContentOption | null>(null);
  const [showManualId, setShowManualId] = React.useState(false);
  const requestIdRef = React.useRef(0);

  const selectedId = value.trim();
  const isAllPosts = !selectedId;

  const loadOptions = React.useCallback(async (append: boolean, cursor?: string | null) => {
    const requestId = ++requestIdRef.current;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError(null);
    }

    try {
      const result = await listCommentAutomationContentOptions({
        channel,
        channelAccountId: channelAccountId || null,
        limit: 24,
        after: cursor || null,
      });

      if (requestId !== requestIdRef.current) {
        return;
      }

      setItems((current) => {
        if (!append) {
          return result.items;
        }
        const seen = new Set(current.map((item) => item.externalContentId));
        return [
          ...current,
          ...result.items.filter((item) => !seen.has(item.externalContentId)),
        ];
      });
      setNextCursor(result.nextCursor ?? null);
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      const message = err instanceof Error && err.message ? err.message : t('postPickerLoadFailed');
      setError(message);
      if (!append) {
        setItems([]);
        setNextCursor(null);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [channel, channelAccountId, t]);

  React.useEffect(() => {
    setSelectedPreview(null);
    setIsOpen(false);
    setItems([]);
    setNextCursor(null);
    setError(null);
    setShowManualId(false);
  }, [channel, channelAccountId]);

  React.useEffect(() => {
    if (!selectedId) {
      setSelectedPreview(null);
      return;
    }

    setSelectedPreview((current) => (
      current?.externalContentId === selectedId
        ? current
        : items.find((item) => item.externalContentId === selectedId) ?? current
    ));
  }, [items, selectedId]);

  React.useEffect(() => {
    if (!selectedId) {
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const result = await listCommentAutomationContentOptions({
          channel,
          channelAccountId: channelAccountId || null,
          limit: 24,
        });
        if (cancelled) {
          return;
        }
        const match = result.items.find((item) => item.externalContentId === selectedId);
        if (!match) {
          return;
        }
        setSelectedPreview(match);
      } catch {
        // Keep the selected id without preview when listing fails.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [channel, channelAccountId, selectedId]);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }
    void loadOptions(false);
  }, [isOpen, loadOptions]);

  const handleSelectAll = () => {
    onChange('', null);
    setSelectedPreview(null);
    setIsOpen(false);
  };

  const handleSelectOption = (option: CommentAutomationContentOption) => {
    onChange(option.externalContentId, option);
    setSelectedPreview(option);
    setIsOpen(false);
  };

  return (
    <div className={styles.picker}>
      <div className={styles.labelRow}>
        <span className={styles.label}>{t('postTarget')}</span>
      </div>
      <p className={styles.hint}>{t('postPickerHint')}</p>

      <div className={styles.selectionRow}>
        <button
          type="button"
          className={`${styles.choiceChip} ${isAllPosts ? styles.choiceChipActive : ''}`}
          onClick={handleSelectAll}
          disabled={disabled}
        >
          {t('allPosts')}
        </button>
        <button
          type="button"
          className={`${styles.choiceChip} ${!isAllPosts ? styles.choiceChipActive : ''}`}
          onClick={() => setIsOpen((open) => !open)}
          disabled={disabled}
        >
          {isOpen ? t('postPickerHide') : t('postPickerChoose')}
        </button>
      </div>

      {!isAllPosts ? (
        <div className={styles.selectedCard}>
          {selectedPreview?.thumbnailUrl ? (
            <img
              className={styles.selectedThumb}
              src={selectedPreview.thumbnailUrl}
              alt={summarizeCaption(selectedPreview.caption) || t('postPickerImageAlt')}
            />
          ) : (
            <div className={styles.selectedThumbPlaceholder} aria-hidden="true">
              {channel === 'messenger' ? 'FB' : 'IG'}
            </div>
          )}
          <div className={styles.selectedBody}>
            <div className={styles.selectedEyebrow}>
              {selectedPreview ? t(contentTypeLabelKey(selectedPreview)) : t('specificPost')}
            </div>
            <p className={styles.selectedCaption}>
              {summarizeCaption(selectedPreview?.caption) || t('postPickerNoCaption')}
            </p>
            {selectedPreview?.permalink ? (
              <a
                className={styles.selectedLink}
                href={selectedPreview.permalink}
                target="_blank"
                rel="noreferrer noopener"
              >
                {t('postPickerViewPost')}
              </a>
            ) : null}
            <button
              type="button"
              className={styles.clearLink}
              onClick={handleSelectAll}
              disabled={disabled}
            >
              {t('postPickerClear')}
            </button>
          </div>
        </div>
      ) : null}

      {isOpen ? (
        <div className={styles.panel}>
          {loading ? (
            <div className={styles.panelState}>
              <Spinner />
              <span>{t('postPickerLoading')}</span>
            </div>
          ) : null}

          {!loading && error ? (
            <div className={styles.panelState}>
              <p className={styles.errorText}>{error}</p>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => void loadOptions(false)}
              >
                {t('postPickerRetry')}
              </button>
            </div>
          ) : null}

          {!loading && !error && items.length === 0 ? (
            <div className={styles.panelState}>
              <p>{t('postPickerEmpty')}</p>
            </div>
          ) : null}

          {!loading && !error && items.length > 0 ? (
            <>
              <div className={styles.grid}>
                {items.map((item) => {
                  const active = item.externalContentId === selectedId;
                  return (
                    <button
                      key={item.externalContentId}
                      type="button"
                      className={`${styles.optionCard} ${active ? styles.optionCardActive : ''}`}
                      onClick={() => handleSelectOption(item)}
                      disabled={disabled}
                    >
                      {item.thumbnailUrl ? (
                        <img
                          className={styles.optionThumb}
                          src={item.thumbnailUrl}
                          alt={summarizeCaption(item.caption) || t('postPickerImageAlt')}
                        />
                      ) : (
                        <div className={styles.optionThumbPlaceholder} aria-hidden="true">
                          {channel === 'messenger' ? 'FB' : 'IG'}
                        </div>
                      )}
                      <div className={styles.optionBody}>
                        <span className={styles.optionType}>{t(contentTypeLabelKey(item))}</span>
                        <span className={styles.optionCaption}>
                          {summarizeCaption(item.caption) || t('postPickerNoCaption')}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
              {nextCursor ? (
                <div className={styles.loadMoreRow}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={loadingMore || disabled}
                    onClick={() => void loadOptions(true, nextCursor)}
                  >
                    {loadingMore ? t('postPickerLoadingMore') : t('postPickerLoadMore')}
                  </button>
                </div>
              ) : null}
            </>
          ) : null}

          <div className={styles.manualBlock}>
            <button
              type="button"
              className={styles.manualToggle}
              onClick={() => setShowManualId((value) => !value)}
            >
              {showManualId ? t('postPickerHideManualId') : t('postPickerShowManualId')}
            </button>
            {showManualId ? (
              <label className={styles.manualField}>
                {t('externalContentId')}
                <input
                  className="text-input"
                  value={value}
                  disabled={disabled}
                  onChange={(event) => {
                    const next = event.target.value;
                    onChange(next, next.trim() ? selectedPreview : null);
                  }}
                  placeholder={t('externalContentIdPlaceholder')}
                />
              </label>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CommentAutomationPostPicker;
